/**
 * Модуль «Апартаменты»: Airbnb и Booking (docs/STAYS.md).
 *
 * Четыре вкладки: брони, общая база квартир, встроенный браузер с
 * аккаунтами площадок, управление аккаунтами (для администратора).
 *
 * Запись брони со страницы сайта или из карточки квартиры переводит на
 * вкладку «Брони» с открытой формой: страница площадки рисуется оболочкой
 * поверх окна, и диалог над ней был бы не виден.
 */

import { useCallback, useEffect, useState } from 'react';
import type { StayAccountDto, StayPersonDto } from '@tw/shared-types';
import { useSession } from '../state/session.tsx';
import { describeError } from '../lib/errors.ts';
import { cn } from '@/lib/utils';
import { AccountsPanel } from '../components/stays/AccountsPanel.tsx';
import { BookingsPanel } from '../components/stays/BookingsPanel.tsx';
import type { BookingPrefill } from '../components/stays/BookingDialog.tsx';
import { PropertiesPanel } from '../components/stays/PropertiesPanel.tsx';
import { StaysBrowser } from '../components/stays/StaysBrowser.tsx';
import { MobileStaysTools } from '../components/stays/MobileStaysTools.tsx';
import { isDesktop } from '../lib/desktop.ts';
import { useIsMobile } from '../lib/useMedia.ts';

type Tab = 'bookings' | 'properties' | 'browser' | 'accounts';

const TAB_KEY = 'tw.stays.tab';
const ACCOUNT_KEY = 'tw.stays.account';

function remembered<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T | null) ?? fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // приватное окно или запрет хранилища — выбор просто не запомнится
  }
}

export function StaysView() {
  const { api, can } = useSession();
  const mobile = useIsMobile();
  const [tab, setTabState] = useState<Tab>(() => remembered<Tab>(TAB_KEY, 'bookings'));
  const [accounts, setAccounts] = useState<StayAccountDto[]>([]);
  const [people, setPeople] = useState<StayPersonDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountIdState] = useState<string | null>(() => remembered<string>(ACCOUNT_KEY, '') || null);
  const [bookingPrefill, setBookingPrefill] = useState<BookingPrefill | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);

  const setTab = (t: Tab) => {
    setTabState(t);
    remember(TAB_KEY, t);
  };
  const setAccountId = (id: string) => {
    setAccountIdState(id);
    remember(ACCOUNT_KEY, id);
  };

  const loadShared = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.stayAccounts(), api.stayPeople()]);
      setAccounts(a.items);
      setPeople(p.items);
    } catch (err) {
      setError(describeError(err));
    }
  }, [api]);

  useEffect(() => {
    void loadShared();
  }, [loadShared]);

  const tabs: Array<{ key: Tab; label: string; visible: boolean }> = [
    { key: 'bookings', label: 'Брони', visible: true },
    { key: 'properties', label: 'База квартир', visible: true },
    // На телефоне — облегчённый «Браузер»: открыть площадку, запись брони из текста.
    { key: 'browser', label: 'Браузер', visible: isDesktop() || mobile },
    { key: 'accounts', label: 'Аккаунты', visible: can('stays.account.manage') },
  ];
  const shown = tabs.filter((t) => t.visible);
  const current = shown.some((t) => t.key === tab) ? tab : 'bookings';

  const book = (prefill: BookingPrefill) => {
    setBookingPrefill(prefill);
    setTab('bookings');
  };

  /**
   * Ссылку площадки — во встроенный браузер, если он есть, и в тот аккаунт,
   * с которого бронировали: квитанция и переписка видны только под ним.
   */
  const openUrl = isDesktop()
    ? (url: string, viaAccountId?: string | null) => {
        if (viaAccountId && accounts.some((a) => a.id === viaAccountId && a.enabled)) setAccountId(viaAccountId);
        setPendingUrl(url);
        setTab('browser');
      }
    : undefined;

  const clearPrefill = useCallback(() => setBookingPrefill(null), []);
  const clearPending = useCallback(() => setPendingUrl(null), []);

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden',
        !mobile && 'rounded-2xl border border-border bg-card/60 shadow-md backdrop-blur-sm',
      )}
    >
      <header
        className={cn(
          'flex-none border-b border-border/70',
          mobile ? 'bg-card/70 px-4 pt-1' : 'bg-gradient-to-b from-secondary/50 to-transparent px-6 pb-3 pt-5',
        )}
      >
        {/* На телефоне название раздела уже в шапке приложения — здесь только вкладки. */}
        <div className={cn('flex flex-wrap items-baseline gap-x-3 gap-y-1', mobile && 'hidden')}>
          <h1 className="text-[26px] font-semibold leading-[1.1] tracking-[-0.027em] text-heading">Апартаменты</h1>
          <p className="max-w-[62ch] text-[13px] leading-relaxed text-muted-foreground">
            Подбор и бронь жилья для работников на Airbnb и Booking: брони, общая база квартир с отзывами и чёрным списком,
            аккаунты компании.
          </p>
        </div>
        <nav className={cn('flex flex-none gap-4 overflow-x-auto', mobile ? 'mt-1 flex-nowrap' : '-mb-3 mt-3 flex-wrap')}>
          {shown.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-current={current === t.key ? 'page' : undefined}
              className={cn(
                'relative whitespace-nowrap border-transparent bg-transparent px-0.5 pb-2.5 text-[13px] font-medium transition-colors',
                current === t.key
                  ? "text-brand after:absolute after:inset-x-0 after:bottom-0 after:h-[2px] after:rounded-full after:bg-brand after:content-['']"
                  : 'text-muted-foreground hover:text-heading',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      {error && (
        <p className="mx-6 mt-3 flex-none rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}

      <div
        key={current}
        className={cn(
          mobile ? 'view-enter min-h-0 flex-1 px-3 pb-4 pt-3' : 'view-enter min-h-0 flex-1 px-6 pb-6 pt-4',
          // Браузеру — вся высота без прокрутки: страница площадки рисуется
          // в его области, и прокрутка вокруг неё сбивала бы координаты.
          current === 'browser' && isDesktop() ? 'flex flex-col overflow-hidden' : 'overflow-y-auto',
        )}
      >
        {current === 'bookings' && (
          <BookingsPanel
            accounts={accounts}
            people={people}
            prefill={bookingPrefill}
            onPrefillUsed={clearPrefill}
            onOpenUrl={openUrl}
          />
        )}
        {current === 'properties' && (
          <PropertiesPanel onBook={(property) => book({ property })} onOpenUrl={openUrl} />
        )}
        {current === 'browser' && !isDesktop() && (
          <MobileStaysTools
            accounts={accounts}
            people={people}
            accountId={accountId}
            onAccountChange={setAccountId}
            onBook={book}
          />
        )}
        {current === 'browser' && isDesktop() && (
          <StaysBrowser
            accounts={accounts}
            people={people}
            accountId={accountId}
            onAccountChange={setAccountId}
            pendingUrl={pendingUrl}
            onPendingUsed={clearPending}
            onBook={book}
          />
        )}
        {current === 'accounts' && <AccountsPanel onChanged={() => void loadShared()} />}
      </div>
    </div>
  );
}
